/**
 * Zeek Field Reference Data
 * Shared data structure for field definitions used in query autocomplete and field guide
 */

export const fieldCategories = [
  {
    name: 'Meta Fields',
    description: 'Meta fields are convenience aliases that automatically expand to search multiple fields at once. For example, port:443 expands to (id.orig_p:443 OR id.resp_p:443).',
    fields: [
      { name: 'port', type: 'Meta', description: 'Source or destination port → id.orig_p, id.resp_p' },
      { name: 'ip', type: 'Meta', description: 'Source or destination IP → id.orig_h, id.resp_h' },
      { name: 'host', type: 'Meta', description: 'HTTP host, DNS query, or TLS/SSL server name → host, query, server_name' },
      { name: 'domain', type: 'Meta', description: 'Same as host → host, query, server_name' },
      { name: 'hash', type: 'Meta', description: 'File hash (any type) → md5, sha1, sha256, hash' },
      { name: 'username', type: 'Meta', description: 'Username across all protocols → user, username, client' },
      { name: 'src_ip', type: 'Meta', description: 'Alias for id.orig_h (source/originating IP)' },
      { name: 'dst_ip', type: 'Meta', description: 'Alias for id.resp_h (destination/responding IP)' },
      { name: 'src_port', type: 'Meta', description: 'Alias for id.orig_p (source port)' },
      { name: 'dst_port', type: 'Meta', description: 'Alias for id.resp_p (destination port)' },
      { name: 'dns_name', type: 'Meta', description: 'Alias for query (DNS query name)' },
      { name: 'filename', type: 'Meta', description: 'Filename from files or SMB → filename, name' },
      { name: 'email', type: 'Meta', description: 'Email address (from/to) → mailfrom, rcptto' },
      { name: 'url', type: 'Meta', description: 'HTTP URL components → host, uri, referrer' },
      { name: 'ua', type: 'Meta', description: 'Alias for user_agent (HTTP user agent)' },
      { name: 'sni', type: 'Meta', description: 'Alias for server_name (TLS/SSL Server Name Indication)' },
      { name: 'cn', type: 'Meta', description: 'Alias for subject (X.509 certificate subject)' },
      { name: 'san', type: 'Meta', description: 'X.509 Subject Alternative Name → san.dns, san.uri, san.email, san.ip' },
      { name: 'mime', type: 'Meta', description: 'Alias for mime_type (MIME type)' }
    ]
  },
  {
    name: 'Common Connection Fields',
    fields: [
      { name: 'ts', type: 'Timestamp', description: 'Event timestamp - present in all Zeek logs (conn, dns, http, smtp, ssh, ssl, files, weird, notice)' },
      { name: 'uid', type: 'String', description: 'Unique connection identifier - present in most logs (conn, dns, http, smtp, ssh, ssl, weird, notice)' },
      { name: 'id.orig_h', type: 'IP Address', description: 'Source/originating IP address - present in conn, dns, http, smtp, ssh, ssl, weird, notice logs' },
      { name: 'id.orig_p', type: 'Port', description: 'Source port number - present in conn, dns, http, smtp, ssh, ssl, weird, notice logs' },
      { name: 'id.resp_h', type: 'IP Address', description: 'Destination/responding IP address - present in conn, dns, http, smtp, ssh, ssl, weird, notice logs' },
      { name: 'id.resp_p', type: 'Port', description: 'Destination port number - present in conn, dns, http, smtp, ssh, ssl, weird, notice logs' },
      { name: 'proto', type: 'String', description: 'Transport layer protocol (tcp, udp, icmp) - present in conn, dns, notice logs' }
    ]
  },
  {
    name: 'Network Connections',
    fields: [
      { name: 'service', type: 'String', description: 'Dynamically detected application protocol' },
      { name: 'duration', type: 'Number', description: 'Duration of the connection in seconds' },
      { name: 'orig_bytes', type: 'Number', description: 'Payload bytes sent by originator' },
      { name: 'resp_bytes', type: 'Number', description: 'Payload bytes sent by responder' },
      { name: 'conn_state', type: 'String', description: 'Connection state (S0=attempt, SF=normal, REJ=rejected, etc.)' },
      { name: 'local_orig', type: 'Boolean', description: 'True if connection originated locally' },
      { name: 'local_resp', type: 'Boolean', description: 'True if responder is local' },
      { name: 'missed_bytes', type: 'Number', description: 'Bytes missed in content gaps (packet loss)' },
      { name: 'history', type: 'String', description: 'State history of the connection' },
      { name: 'orig_pkts', type: 'Number', description: 'Number of packets from originator' },
      { name: 'orig_ip_bytes', type: 'Number', description: 'IP-level bytes from originator (including headers)' },
      { name: 'resp_pkts', type: 'Number', description: 'Number of packets from responder' },
      { name: 'resp_ip_bytes', type: 'Number', description: 'IP-level bytes from responder (including headers)' },
      { name: 'tunnel_parents', type: 'Set', description: 'UIDs of encapsulating parent connections if tunneled' },
      { name: 'ip_proto', type: 'Number', description: 'IP protocol number of the connection' },
      { name: 'vlan', type: 'Number', description: 'Outer VLAN tag if present' },
      { name: 'inner_vlan', type: 'Number', description: 'Inner VLAN tag if present' }
    ]
  },
  {
    name: 'DNS',
    fields: [
      { name: 'trans_id', type: 'Number', description: 'DNS transaction identifier' },
      { name: 'query', type: 'String', description: 'Domain name being queried' },
      { name: 'qclass', type: 'Number', description: 'Query class (1=IN for Internet)' },
      { name: 'qclass_name', type: 'String', description: 'Query class name' },
      { name: 'qtype', type: 'Number', description: 'Query type code' },
      { name: 'qtype_name', type: 'String', description: 'Query type (A, AAAA, CNAME, MX, TXT, PTR, etc.)' },
      { name: 'rcode', type: 'Number', description: 'Response code number' },
      { name: 'rcode_name', type: 'String', description: 'Response code (NOERROR, NXDOMAIN, SERVFAIL, etc.)' },
      { name: 'AA', type: 'Boolean', description: 'Authoritative Answer flag' },
      { name: 'TC', type: 'Boolean', description: 'Truncation flag' },
      { name: 'RD', type: 'Boolean', description: 'Recursion Desired flag' },
      { name: 'RA', type: 'Boolean', description: 'Recursion Available flag' },
      { name: 'answers', type: 'Array', description: 'DNS response answers' },
      { name: 'TTLs', type: 'Array', description: 'Time-to-live values for answers' },
      { name: 'rejected', type: 'Boolean', description: 'Query was rejected by server' }
    ]
  },
  {
    name: 'HTTP',
    fields: [
      { name: 'trans_depth', type: 'Number', description: 'Pipelined request/response depth (http, smtp)' },
      { name: 'method', type: 'String', description: 'HTTP method (GET, POST, PUT, DELETE, HEAD, etc.)' },
      { name: 'host', type: 'String', description: 'Host header value' },
      { name: 'uri', type: 'String', description: 'URI requested (path and query string)' },
      { name: 'referrer', type: 'String', description: 'Referer header value' },
      { name: 'version', type: 'String', description: 'HTTP version (1.0, 1.1, 2.0)' },
      { name: 'user_agent', type: 'String', description: 'User-Agent header identifying client software' },
      { name: 'origin', type: 'String', description: 'Origin header for CORS requests' },
      { name: 'request_body_len', type: 'Number', description: 'Length of HTTP request body' },
      { name: 'response_body_len', type: 'Number', description: 'Length of HTTP response body' },
      { name: 'status_code', type: 'Number', description: 'HTTP response status code (200, 404, 500, etc.)' },
      { name: 'status_msg', type: 'String', description: 'HTTP response status message' },
      { name: 'info_code', type: 'Number', description: 'Informational response code' },
      { name: 'info_msg', type: 'String', description: 'Informational message' },
      { name: 'tags', type: 'Set', description: 'Indicators for specific HTTP behaviors' },
      { name: 'username', type: 'String', description: 'Username from authentication' },
      { name: 'password', type: 'String', description: 'Password from authentication (if captured)' },
      { name: 'proxied', type: 'Set', description: 'Proxy headers indicating proxied connections' },
      { name: 'orig_fuids', type: 'Array', description: 'File UIDs from originator' },
      { name: 'orig_filenames', type: 'Array', description: 'Filenames from originator' },
      { name: 'orig_mime_types', type: 'Array', description: 'MIME types from originator' },
      { name: 'resp_fuids', type: 'Array', description: 'File UIDs from responder' },
      { name: 'resp_filenames', type: 'Array', description: 'Filenames from responder' },
      { name: 'resp_mime_types', type: 'Array', description: 'MIME types from responder' }
    ]
  },
  {
    name: 'Email (SMTP)',
    fields: [
      { name: 'helo', type: 'String', description: 'HELO/EHLO string sent by client' },
      { name: 'mailfrom', type: 'String', description: 'MAIL FROM address (sender)' },
      { name: 'rcptto', type: 'Set', description: 'RCPT TO addresses (recipients)' },
      { name: 'date', type: 'Timestamp', description: 'Date header from message' },
      { name: 'from', type: 'String', description: 'From header address' },
      { name: 'to', type: 'Set', description: 'To header addresses' },
      { name: 'cc', type: 'Set', description: 'CC header addresses' },
      { name: 'reply_to', type: 'String', description: 'Reply-To header address' },
      { name: 'msg_id', type: 'String', description: 'Message-ID header value' },
      { name: 'in_reply_to', type: 'String', description: 'In-Reply-To header value' },
      { name: 'subject', type: 'String', description: 'Email subject line' },
      { name: 'x_originating_ip', type: 'IP Address', description: 'X-Originating-IP header value' },
      { name: 'first_received', type: 'String', description: 'First Received header' },
      { name: 'second_received', type: 'String', description: 'Second Received header' },
      { name: 'last_reply', type: 'String', description: 'Last SMTP reply code and message' },
      { name: 'path', type: 'Array', description: 'Message transmission path (servers)' },
      { name: 'user_agent', type: 'String', description: 'X-Mailer or User-Agent header' },
      { name: 'tls', type: 'Boolean', description: 'Connection used STARTTLS' },
      { name: 'fuids', type: 'Set', description: 'File UIDs of attached files' },
      { name: 'is_webmail', type: 'Boolean', description: 'Email sent via webmail' }
    ]
  },
  {
    name: 'SSH',
    fields: [
      { name: 'version', type: 'Number', description: 'SSH protocol version (1 or 2)' },
      { name: 'auth_success', type: 'Boolean', description: 'Authentication was successful' },
      { name: 'auth_attempts', type: 'Number', description: 'Number of authentication attempts' },
      { name: 'direction', type: 'String', description: 'Direction of connection (INBOUND/OUTBOUND)' },
      { name: 'client', type: 'String', description: 'SSH client software string' },
      { name: 'server', type: 'String', description: 'SSH server software string' },
      { name: 'cipher_alg', type: 'String', description: 'Encryption algorithm negotiated' },
      { name: 'mac_alg', type: 'String', description: 'MAC (message authentication code) algorithm' },
      { name: 'compression_alg', type: 'String', description: 'Compression algorithm negotiated' },
      { name: 'kex_alg', type: 'String', description: 'Key exchange algorithm' },
      { name: 'host_key_alg', type: 'String', description: 'Server host key algorithm' },
      { name: 'host_key', type: 'String', description: 'Server host key fingerprint' },
      { name: 'remote_location.country_code', type: 'String', description: 'GeoIP country code of remote host' },
      { name: 'remote_location.region', type: 'String', description: 'GeoIP region of remote host' },
      { name: 'remote_location.city', type: 'String', description: 'GeoIP city of remote host' },
      { name: 'remote_location.latitude', type: 'Number', description: 'GeoIP latitude of remote host' },
      { name: 'remote_location.longitude', type: 'Number', description: 'GeoIP longitude of remote host' }
    ]
  },
  {
    name: 'SSL/TLS',
    fields: [
      { name: 'version', type: 'String', description: 'SSL/TLS version (SSLv2, SSLv3, TLSv1.0, TLSv1.2, TLSv1.3)' },
      { name: 'cipher', type: 'String', description: 'Cipher suite negotiated' },
      { name: 'curve', type: 'String', description: 'Elliptic curve used for key exchange' },
      { name: 'server_name', type: 'String', description: 'Server name from SNI extension' },
      { name: 'resumed', type: 'Boolean', description: 'Session was resumed' },
      { name: 'last_alert', type: 'String', description: 'Last TLS alert seen' },
      { name: 'next_protocol', type: 'String', description: 'Next protocol negotiated (ALPN/NPN)' },
      { name: 'established', type: 'Boolean', description: 'Connection was fully established' },
      { name: 'cert_chain_fuids', type: 'Array', description: 'File UIDs for certificate chain' },
      { name: 'client_cert_chain_fuids', type: 'Array', description: 'File UIDs for client certificate chain' },
      { name: 'subject', type: 'String', description: 'Certificate subject (usually domain name)' },
      { name: 'issuer', type: 'String', description: 'Certificate issuer (CA name)' },
      { name: 'client_subject', type: 'String', description: 'Client certificate subject' },
      { name: 'client_issuer', type: 'String', description: 'Client certificate issuer' },
      { name: 'validation_status', type: 'String', description: 'Certificate validation result' }
    ]
  },
  {
    name: 'X.509 Certificates',
    fields: [
      { name: 'id', type: 'String', description: 'Certificate file ID (x509)' },
      { name: 'certificate.version', type: 'Number', description: 'X.509 version (usually 3)' },
      { name: 'certificate.serial', type: 'String', description: 'Serial number of certificate' },
      { name: 'certificate.subject', type: 'String', description: 'Subject DN (domain/organization)' },
      { name: 'certificate.issuer', type: 'String', description: 'Issuer DN (Certificate Authority)' },
      { name: 'certificate.not_valid_before', type: 'Timestamp', description: 'Certificate validity start time' },
      { name: 'certificate.not_valid_after', type: 'Timestamp', description: 'Certificate expiration time' },
      { name: 'certificate.key_alg', type: 'String', description: 'Public key algorithm (RSA, ECDSA)' },
      { name: 'certificate.sig_alg', type: 'String', description: 'Signature algorithm' },
      { name: 'certificate.key_type', type: 'String', description: 'Key type' },
      { name: 'certificate.key_length', type: 'Number', description: 'Public key length in bits' },
      { name: 'certificate.exponent', type: 'String', description: 'RSA public exponent' },
      { name: 'certificate.curve', type: 'String', description: 'Elliptic curve name' },
      { name: 'san.dns', type: 'Array', description: 'Subject Alternative Names (DNS)' },
      { name: 'san.uri', type: 'Array', description: 'Subject Alternative Names (URI)' },
      { name: 'san.email', type: 'Array', description: 'Subject Alternative Names (email)' },
      { name: 'san.ip', type: 'Array', description: 'Subject Alternative Names (IP)' },
      { name: 'basic_constraints.ca', type: 'Boolean', description: 'Certificate is a CA' },
      { name: 'basic_constraints.path_len', type: 'Number', description: 'Maximum CA path length' }
    ]
  },
  {
    name: 'TLS Fingerprints (JA3/JA4)',
    fields: [
      { name: 'ja3', type: 'String', description: 'JA3 TLS client fingerprint - MD5 hash of TLS client hello parameters (legacy)' },
      { name: 'ja3s', type: 'String', description: 'JA3S TLS server fingerprint - MD5 hash of TLS server hello parameters (legacy)' },
      { name: 'ja4', type: 'String', description: 'JA4 TLS client fingerprint - improved fingerprinting with better normalization' },
      { name: 'ja4s', type: 'String', description: 'JA4 server response fingerprint - server-side JA4 variant' },
      { name: 'ja4h', type: 'String', description: 'JA4 HTTP client fingerprint - based on HTTP header order and values' },
      { name: 'ja4l', type: 'String', description: 'JA4 light/latency fingerprint - timing-based characteristics' },
      { name: 'ja4ls', type: 'String', description: 'JA4 latency server fingerprint - server-side latency patterns' },
      { name: 'ja4t', type: 'String', description: 'JA4 TCP client fingerprint - based on TCP connection characteristics' },
      { name: 'ja4ts', type: 'String', description: 'JA4 TCP server fingerprint - server-side TCP patterns' }
    ]
  },
  {
    name: 'File Transfers',
    fields: [
      { name: 'fuid', type: 'String', description: 'Unique file identifier (files, notice)' },
      { name: 'tx_hosts', type: 'Set', description: 'IPs that transmitted file' },
      { name: 'rx_hosts', type: 'Set', description: 'IPs that received file' },
      { name: 'conn_uids', type: 'Set', description: 'Connection UIDs associated with file' },
      { name: 'source', type: 'String', description: 'Protocol source (HTTP, SMTP, FTP, etc.)' },
      { name: 'depth', type: 'Number', description: 'Depth into connection' },
      { name: 'analyzers', type: 'Set', description: 'File analyzers applied' },
      { name: 'mime_type', type: 'String', description: 'MIME type of file' },
      { name: 'filename', type: 'String', description: 'Filename if available' },
      { name: 'duration', type: 'Number', description: 'Duration of file transfer' },
      { name: 'local_orig', type: 'Boolean', description: 'File originated locally' },
      { name: 'is_orig', type: 'Boolean', description: 'File sent by connection originator' },
      { name: 'seen_bytes', type: 'Number', description: 'Bytes seen of file' },
      { name: 'total_bytes', type: 'Number', description: 'Total bytes of file (if known)' },
      { name: 'missing_bytes', type: 'Number', description: 'Bytes missing due to packet loss' },
      { name: 'overflow_bytes', type: 'Number', description: 'Bytes over file size limit' },
      { name: 'timedout', type: 'Boolean', description: 'Transfer timed out' },
      { name: 'parent_fuid', type: 'String', description: 'Parent file UID (for archives)' },
      { name: 'md5', type: 'String', description: 'MD5 hash of file' },
      { name: 'sha1', type: 'String', description: 'SHA1 hash of file' },
      { name: 'sha256', type: 'String', description: 'SHA256 hash of file' },
      { name: 'extracted', type: 'String', description: 'Path where file was extracted' },
      { name: 'extracted_cutoff', type: 'Boolean', description: 'Extraction was cut off' },
      { name: 'extracted_size', type: 'Number', description: 'Size of extracted file' }
    ]
  }
]
